import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
// @ts-ignore
import { ResizableBox, ResizableCallbackData } from "react-resizable";
import "react-resizable/css/styles.css";

// データ型
export type UserListItem = {
  id: string | number;
  userNumber?: number;
  lastName?: string;
  firstName?: string;
  mailAddress?: string;
  baseId?: number;
  base?: string;
  baseIds?: number[];
  bases?: string[];
  shopName?: string;
  roomId?: number;
  driverName?: string;
  approval?: boolean;
  memo?: string;
  isHopeWarehouse?: boolean;
  isHopeFsz1?: boolean;
  isHopeHnd2?: boolean;
  isHopeHnd3?: boolean;
  isHopeHnd6?: boolean;
  isHopeHnd9?: boolean;
  isHopeHsg1?: boolean;
  isHopeKix1?: boolean;
  isHopeKix2?: boolean;
  isHopeKix3?: boolean;
  isHopeKix4?: boolean;
  isHopeKix5?: boolean;
  isHopeKix6?: boolean;
  isHopeNrt1?: boolean;
  isHopeNrt2?: boolean;
  isHopeNrt5?: boolean;
  isHopeNgo2?: boolean;
  isHopeQcb1?: boolean;
  isHopeQcb3?: boolean;
  isHopeQcb4?: boolean;
  isHopeQcb5?: boolean;
  isHopeTpb1?: boolean;
  isHopeTpb2?: boolean;
  isHopeTpb3?: boolean;
  isHopeTpb5?: boolean;
  isHopeTpb9?: boolean;
  isHopeTpf2?: boolean;
  isHopeTpf3?: boolean;
  isHopeTpf4?: boolean;
  isHopeTpf6?: boolean;
  isHopeTpf9?: boolean;
  isHopeTpf_a?: boolean;
  isHopeTpf_b?: boolean;
  isHopeTpf_c?: boolean;
  isHopeTpf_d?: boolean;
  isHopeTpf_i?: boolean;
  isHopeTpn1?: boolean;
  isHopeTpn2?: boolean;
  isHopeTpn3?: boolean;
  isHopeTpx1?: boolean;
  isHopeTpx2?: boolean;
  isHopeTpy1?: boolean;
  isHopeTpy2?: boolean;
  isHopeTpy3?: boolean;
  isHopeTpy5?: boolean;
  isHopeTpz1?: boolean;
  isHopeTpz2?: boolean;
  isHopeTpz3?: boolean;
  isHopeTpz4?: boolean;
  isHopeTpz5?: boolean;
  isHopeTpz6?: boolean;
  isHopeTpz7?: boolean;
  isHopeTpz8?: boolean;
  isHopeTpz9?: boolean;
  isHopeTpr1?: boolean;
  isHopeTyo1?: boolean;
  isHopeTyo2?: boolean;
  isHopeTyo4?: boolean;
  isHopeTyo6?: boolean;
  isHopeTyo7?: boolean;
  isHopeTyo8?: boolean;
  isHopeTyo9?: boolean;
  isHopeVjg_t?: boolean;
  isHopeVjn_a?: boolean;
  isHopeVjn_b?: boolean;
  isHopeVjn_c?: boolean;
  isHopeVjn_d?: boolean;
  isHopeVjn_e?: boolean;
  isHopeVjw_a?: boolean;
  isHopeVjw_b?: boolean;
  isHopeXje1?: boolean;
  isHopeXje2?: boolean;
};

type Props = {
  users: UserListItem[];
  checkedIds: Set<string>;
  onCheckedChange: (id: string) => void;
  onCheckedChangeAll: (checkedIds: Set<string>) => void;
  onRowClick: (user: UserListItem) => void;
  footerHeight: number;
};

const UserListTable: React.FC<Props> = ({
  users,
  checkedIds,
  onCheckedChange,
  onCheckedChangeAll,
  onRowClick,
  footerHeight,
}) => {
  // フィルター条件
  const [filters, setFilters] = useState<Partial<UserListItem>>({});
  const [columnWidths, setColumnWidths] = useState<number[]>([
    30, // チェックボックス
    30, // 承認
    150, // ユーザー
    200, // メール
    120, // 拠点
    150, // ショップ名
    120, // roomId
    150, // 配送名
    200, // メモ
  ]);
  const [approvalVal, setApprovalVal] = useState("");

  // フィルター条件の更新
  const handleFilterChange = (
    key: keyof UserListItem | "fullName",
    value: string
  ) => {
    if (key === "approval") {
      if (value === "true") {
        setApprovalVal("〇");
      } else if (value === "false") {
        setApprovalVal("ー");
      } else {
        setApprovalVal("");
      }
    }
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // フィルタリング
  const filteredUsers = users.filter((user) => {
    return Object.entries(filters).every(([key, value]) => {
      if (!value) return true;
      let targetValue = "";
      if (key === "fullName") {
        targetValue =
          (user.userNumber ?? "") +
          (user.lastName ?? "") +
          (user.firstName ?? "");
      } else {
        const targetValue = user[key as keyof UserListItem] ?? "";
        return String(targetValue).includes(String(value));
      }
      return String(targetValue).includes(String(value));
    });
  });

  // 列幅更新
  const handleResize = (index: number, newWidth: number) => {
    const updated = [...columnWidths];
    updated[index] = newWidth;
    setColumnWidths(updated);
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflow: "auto",
        height: `calc(100vh - 80px - ${
          checkedIds.size > 0 ? footerHeight * 1.3 : 0
        }px)`,
        overflowY: "auto",
        overflowX: "auto",
      }}
    >
      <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
        <TableHead>
          {/* ヘッダー行 */}
          <TableRow>
            <TableCell align="center" sx={{ width: 20, padding: 0 }}>
              <Checkbox
                checked={
                  checkedIds.size === filteredUsers.length &&
                  filteredUsers.length > 0
                }
                indeterminate={
                  checkedIds.size > 0 && checkedIds.size < filteredUsers.length
                }
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (checked) {
                    const newSet = new Set(
                      filteredUsers.map((user) => String(user.id))
                    );
                    onCheckedChangeAll(newSet);
                  } else {
                    onCheckedChangeAll(new Set());
                  }
                }}
              />
            </TableCell>
            {[
              "承認",
              "ユーザー",
              "メールアドレス",
              "拠点",
              "ショップ名",
              "roomId",
              "配送名",
              "メモ",
            ].map((label, idx) => (
              <TableCell
                key={label}
                align="center"
                style={{ width: columnWidths[idx + 1], whiteSpace: "nowrap" }}
              >
                <ResizableBox
                  width={columnWidths[idx + 1]}
                  height={30}
                  axis="x"
                  resizeHandles={["e"]}
                  onResizeStop={(_: unknown, data: ResizableCallbackData) => {
                    handleResize(idx + 1, data.size.width);
                  }}
                >
                  <div
                    style={{
                      width: columnWidths[idx + 1],
                      textAlign: "center",
                    }}
                  >
                    {label}
                  </div>
                </ResizableBox>
              </TableCell>
            ))}
          </TableRow>

          {/* フィルター行 */}
          <TableRow>
            <TableCell />
            <TableCell>
              <Select
                variant="standard"
                fullWidth
                displayEmpty
                value={approvalVal}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "〇") {
                    handleFilterChange("approval", "true");
                  } else if (val === "ー") {
                    handleFilterChange("approval", "false");
                  } else {
                    handleFilterChange("approval", "");
                  }
                }}
              >
                <MenuItem value="">　</MenuItem>
                <MenuItem value="〇">〇</MenuItem>
                <MenuItem value="ー">ー</MenuItem>
              </Select>
            </TableCell>
            <TableCell>
              <TextField
                variant="standard"
                placeholder="フィルター"
                fullWidth
                onChange={(e) => handleFilterChange("fullName", e.target.value)}
              />
            </TableCell>
            <TableCell>
              <TextField
                variant="standard"
                placeholder="フィルター"
                fullWidth
                onChange={(e) =>
                  handleFilterChange("mailAddress", e.target.value)
                }
              />
            </TableCell>
            <TableCell>
              <TextField
                variant="standard"
                placeholder="フィルター"
                fullWidth
                onChange={(e) => handleFilterChange("base", e.target.value)}
              />
            </TableCell>
            <TableCell>
              <TextField
                variant="standard"
                placeholder="フィルター"
                fullWidth
                onChange={(e) => handleFilterChange("shopName", e.target.value)}
              />
            </TableCell>
            <TableCell>
              <TextField
                variant="standard"
                placeholder="フィルター"
                fullWidth
                onChange={(e) => handleFilterChange("roomId", e.target.value)}
              />
            </TableCell>
            <TableCell>
              <TextField
                variant="standard"
                placeholder="フィルター"
                fullWidth
                onChange={(e) =>
                  handleFilterChange("driverName", e.target.value)
                }
              />
            </TableCell>
            <TableCell>
              <TextField
                variant="standard"
                placeholder="フィルター"
                fullWidth
                onChange={(e) => handleFilterChange("memo", e.target.value)}
              />
            </TableCell>
          </TableRow>
        </TableHead>

        {/* データ行 */}
        <TableBody>
          {filteredUsers.map((user) => (
            <TableRow
              key={user.id}
              onClick={() => {
                onRowClick(user);
              }}
            >
              <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={checkedIds.has(String(user.id))}
                  onChange={(e) => {
                    e.stopPropagation();
                    onCheckedChange(String(user.id));
                  }}
                />
              </TableCell>
              <TableCell>{user.approval ? "〇" : "ー"}</TableCell>
              <TableCell>
                {(user.userNumber ?? "") +
                  "_" +
                  (user.lastName ?? "") +
                  (user.firstName ?? "")}
              </TableCell>
              <TableCell>{user.mailAddress}</TableCell>
              <TableCell>{user.base}</TableCell>
              <TableCell>{user.shopName}</TableCell>
              <TableCell>{user.roomId}</TableCell>
              <TableCell>{user.driverName}</TableCell>
              <TableCell>{user.memo}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default UserListTable;
